import { eachDay, dayOfWeek } from "./date";

export const SEASONS = [
  { key: "base", label: "Base" },
  { key: "weekend", label: "Weekend" },
  { key: "holiday", label: "Holiday" },
  { key: "peak", label: "Peak Season" },
];

export const SEASON_COLORS = {
  base: "#64748b",
  weekend: "#2563eb",
  holiday: "#c026d3",
  peak: "#ea580c",
};

export const TIER_FIELDS = ["weekend", "holiday", "peak"];

export const PRICE_CLASSES = [
  { key: "b2b", label: "B2B Price" },
  { key: "selling", label: "Selling Price" },
];

export const MODE_AMOUNT = "amount";
export const MODE_PERCENT = "percent";

export function roomKey(room) {
  if (!room) return "";
  const property = room.propertyName || "";
  const name = room.roomName || "";
  if (property || name) return `${property}::${name}`;
  return room.id ? String(room.id) : "";
}

export function baseRateFor(room) {
  const selling = Number(room?.sellingPrice || 0);
  if (selling > 0) return selling;
  const base = Number(room?.basePrice || 0);
  if (base > 0) return base;
  return Number(room?.price || 0);
}

export function regularRatesFor(room) {
  const fallback = baseRateFor(room);
  const b2b = Number(room?.b2bPrice || 0);
  const selling = Number(room?.sellingPrice || 0);
  return {
    b2b: b2b > 0 ? b2b : fallback,
    selling: selling > 0 ? selling : fallback,
  };
}

export function emptyTier() {
  return {
    weekend: { b2b: 0, selling: 0 },
    holiday: { b2b: 0, selling: 0 },
    peak: { b2b: 0, selling: 0 },
  };
}

export function normalizeTier(raw, regular) {
  const tier = emptyTier();
  if (!raw) return tier;
  const legacyPercent = raw.mode === MODE_PERCENT;

  for (const season of TIER_FIELDS) {
    const entry = raw[season];
    if (entry == null || entry === "") continue;

    if (typeof entry === "object") {
      tier[season].b2b = Number(entry.b2b || 0);
      tier[season].selling = Number(entry.selling || 0);
      continue;
    }

    const legacy = Number(entry);
    if (!Number.isFinite(legacy) || legacy === 0) continue;
    for (const { key } of PRICE_CLASSES) {
      const base = Number(regular?.[key] || 0);
      tier[season][key] = legacyPercent && base > 0 ? Math.round(base * (1 + legacy / 100)) : legacy;
    }
  }

  return tier;
}

export function tierFor(room, tiers) {
  return normalizeTier((tiers || {})[roomKey(room)], regularRatesFor(room));
}

export function resolveTierRates(seasonRates, regular) {
  const resolved = {};
  for (const { key } of PRICE_CLASSES) {
    const override = Number(seasonRates?.[key] || 0);
    resolved[key] = override > 0 ? override : Number(regular?.[key] || 0);
  }
  return resolved;
}

export function parseWeekendDays(raw) {
  const parts = Array.isArray(raw) ? raw : String(raw || "").split(",");
  return [...new Set(
    parts
      .map((value) => String(value).trim())
      .filter((value) => value !== "")
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6)
  )].sort((a, b) => a - b);
}

export function formatWeekendDays(raw) {
  const days = parseWeekendDays(raw);
  return days.length ? days.join(",") : "";
}

export function normalizeHolidays(raw) {
  if (!Array.isArray(raw)) return [];
  const byDate = new Map();
  for (const entry of raw) {
    const item =
      typeof entry === "string"
        ? { date: String(entry).trim(), label: "" }
        : entry && typeof entry === "object"
          ? { date: String(entry.date || "").trim(), label: String(entry.label || "").trim() }
          : null;
    if (!item || !item.date) continue;
    byDate.set(item.date, item);
  }
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

export function holidayForDate(date, holidays) {
  if (!Array.isArray(holidays)) return null;
  for (const entry of holidays) {
    const value = typeof entry === "string" ? entry : entry?.date;
    if (value === date) return typeof entry === "string" ? { date, label: "" } : entry;
  }
  return null;
}

export function propertyCalendar(calendar, propertyName) {
  const entry = (calendar || {})[propertyName || ""];
  return {
    weekendDays: Array.isArray(entry?.weekendDays) ? parseWeekendDays(entry.weekendDays) : null,
    holidays: normalizeHolidays(entry?.holidays),
    peakRanges: Array.isArray(entry?.peakRanges)
      ? entry.peakRanges.filter((range) => range && range.start && range.end)
      : [],
  };
}

export function emptyCalendar() {
  return { weekendDays: null, holidays: [], peakRanges: [] };
}

export function resolveWeekendDays(propertyWeekendDays, globalWeekendDays) {
  if (Array.isArray(propertyWeekendDays)) return propertyWeekendDays;
  return parseWeekendDays(globalWeekendDays);
}

export const SPECIAL_DAY_KEYS = {
  weekend: "SpecialDayWeekend",
  holiday: "SpecialDayHolidays",
  peak: "SpecialDayPeak",
};

export const SPECIAL_DAY_OPTIONS = [
  { key: "weekend", label: "weekend" },
  { key: "holiday", label: "holidays" },
  { key: "peak", label: "peak season" },
];

function isEnabledFlag(value) {
  const text = String(value ?? "").trim().toLowerCase();
  return !(text === "false" || text === "0" || text === "no" || text === "off");
}

export function parseSpecialDays(settings) {
  const raw = settings || {};
  return {
    weekend: isEnabledFlag(raw[SPECIAL_DAY_KEYS.weekend]),
    holiday: isEnabledFlag(raw[SPECIAL_DAY_KEYS.holiday]),
    peak: isEnabledFlag(raw[SPECIAL_DAY_KEYS.peak]),
  };
}

export function formatSpecialDays(specialDays) {
  const state = specialDays || {};
  return Object.entries(SPECIAL_DAY_KEYS).reduce(
    (acc, [key, settingKey]) => {
      acc[settingKey] = String(state[key] !== false);
      return acc;
    },
    {}
  );
}

export function seasonForDate(date, config = {}) {
  const { weekendDays = [], holidays = [], peakRanges = [], specialDays = {} } = config;
  const isOn = (key) => specialDays[key] !== false;
  const inPeak = peakRanges.some(
    (range) => range.start && range.end && date >= range.start && date <= range.end
  );
  if (inPeak && isOn("peak")) return "peak";
  if (holidayForDate(date, holidays) && isOn("holiday")) return "holiday";
  if (weekendDays.includes(dayOfWeek(date)) && isOn("weekend")) return "weekend";
  return "base";
}

export function rateForDate(room, date, config = {}) {
  const calendar = propertyCalendar(config.calendar, room?.propertyName);
  const season = seasonForDate(date, {
    weekendDays: resolveWeekendDays(calendar.weekendDays, config.weekendDays),
    holidays: calendar.holidays,
    peakRanges: calendar.peakRanges,
    specialDays: config.specialDays,
  });

  const holiday = holidayForDate(date, calendar.holidays);
  const holidayLabel = holiday ? holiday.label || "" : "";
  const regular = regularRatesFor(room);
  const tier = tierFor(room, config.tiers);
  const rates = resolveTierRates(tier[season], regular);

  return {
    date,
    season,
    base: regular.selling,
    b2b: rates.b2b,
    selling: rates.selling,
    price: config.priceClass === "b2b" ? rates.b2b : rates.selling,
    holidayLabel,
  };
}

export function quoteStay(room, checkIn, checkOut, config = {}) {
  const nights = eachDay(checkIn, checkOut).map((date) => rateForDate(room, date, config));
  const perNightTotal = nights.reduce((sum, night) => sum + night.price, 0);
  const baseTotal = nights.reduce((sum, night) => sum + night.base, 0);
  return {
    nights,
    nightCount: nights.length,
    perNightTotal,
    baseTotal,
    averageNightly: nights.length ? Math.round(perNightTotal / nights.length) : 0,
    hasSeasonalNights: nights.some((night) => night.price !== night.base),
    hasClassifiedNights: nights.some((night) => night.season !== "base"),
  };
}
